import { useState, useEffect, useRef, Fragment } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage, detectLangFromPath, swapLangPrefix } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { useAudience } from '@/lib/audience';
import { useSameRouteScroll } from '@/lib/sameRouteScroll';
import { useSamePageRefresh } from '@/lib/samePageRefresh';
import { Button } from '@/components/ui/button';
import { Menu, Globe, LogOut, ChevronDown, User, X, Zap, CreditCard, HelpCircle, Mail, Phone, MessageSquare, LayoutDashboard, Settings, Shield, ShieldCheck as ShieldCheckIcon, Crown, FileText, Rocket, Briefcase, Sparkles, BookOpen, Calendar, Gift, Compass, ExternalLink, Building2, Award, Wrench, MapPin, ShieldAlert, Lock, Star, Users, Receipt, Workflow, FileSpreadsheet, Bell, CalendarClock, ClipboardCheck, Languages, AlertTriangle as AlertTriangleIcon, Facebook } from 'lucide-react';
import DevBanner from '@/components/layout/LaunchBanner';
import LaunchCountdownBanner from '@/components/LaunchCountdownBanner';
import MobileEarlyAccessStats from '@/components/layout/MobileEarlyAccessStats';
import InlinePrototypeCounters from '@/components/InlinePrototypeCounters';
import { SITE_NAV_SECTIONS, resolveNavPath, SECTION_COLOR_CLASSES } from '@/data/siteNav';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';


/**
 * AudienceSwitcher — top-bar Personal / Business toggle. Updates the
 * global AudienceContext AND navigates to the corresponding overview
 * page so the user lands somewhere meaningful.
 */
function AudienceSwitcher({ ja, prefix = '' }) {
  const { audience, setAudience } = useAudience();
  const navigate = useNavigate();
  const location = useLocation();
  const [hovered, setHovered] = useState(null);
  const closeTimer = useRef(null);

  // Hide on authenticated surfaces only (dashboard/admin). The
  // audience switcher must remain visible on /login and /register
  // so visitors can jump back to Personal or Business marketing.
  const path = location.pathname || '';
  if (path.startsWith('/dashboard') || path.startsWith('/admin')) {
    return null;
  }

  const openMenu = (key) => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setHovered(key);
  };
  const schedClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setHovered(null), 220);
  };

  // Active-page detection that ignores the /en or /ja language prefix and any
  // trailing slash, so the page you're on stays highlighted in the dropdown.
  const normPath = (p) => (p || '').replace(/^\/(en|ja)(?=\/|$)/, '').replace(/\/+$/, '') || '/';

  // Each audience pill carries its own page directory. Hovering the
  // pill opens a small dropdown of the audience's pages; clicking the
  // pill itself flips the audience context AND navigates to overview.
  // Iter 92 (refined v2) — Audience pills are quick shortcuts. Each
  // dropdown stays SHORT (4-5 items) and audience-focused. No more
  // "View All Platform Features" repeated everywhere — the main
  // header has a dedicated `/features` link for that.
  const tracks = [
    {
      key: 'business', labelJa: '法人', labelEn: 'Business', icon: Building2,
      target: '/business',
      tone: { bg: 'bg-[#9200C7]', text: 'text-[#5b0080]', linkActive: 'text-[#5b0080]', barColor: '#9200C7', hoverBg: 'hover:bg-purple-50', hoverText: 'hover:text-[#5b0080]', activeBg: 'bg-purple-100', activeText: 'text-[#5b0080]' },
      desc: ja ? '法人・車両管理' : 'Fleets · branches · accounting',
      items: [
        { ja: '法人スイート 概要',            en: 'Business Overview',           path: '/business' },
        { ja: '法人スイート 概要',            en: 'Business Features',           path: '/business/features' },
        { ja: '複数カード・拠点別レポート',                     en: 'Multi-Card and Branch Reporting',     path: '/business/organization-management', badge: { ja: '主要機能', en: 'Key' } },
        { ja: '登録車両リスト',     en: 'Registered Vehicle Registry', path: '/business/authorized-vehicles' },
        { ja: '目的外利用の確認',           en: 'Usage requiring review',            path: '/features/authorized-vehicle-monitoring' },
        { ja: '法人スイート 概要',            en: 'Business Pricing',            path: '/business/pricing' },
        { ja: '1年間無料体験を始める',  en: 'Start 1-Year Free Trial',     path: `${prefix}/free-trial` },
      ],
    },
    {
      key: 'personal', labelJa: '個人', labelEn: 'Personal', icon: User,
      target: '/personal',
      tone: { bg: 'bg-emerald-600', text: 'text-emerald-800', linkActive: 'text-emerald-700', barColor: '#16a34a', hoverBg: 'hover:bg-emerald-50', hoverText: 'hover:text-emerald-800', activeBg: 'bg-emerald-100', activeText: 'text-emerald-800' },
      desc: ja ? '個人ドライバー' : 'Individual drivers',
      items: [
        { ja: '個人向け概要',            en: 'Personal Overview',         path: '/personal' },
        { ja: '個人向け機能',            en: 'Personal Features',         path: '/personal/features' },
        { ja: '家族登録車両リスト',     en: 'Family Vehicle List',       path: '/personal/family-vehicle-list' },
        { ja: '個人プラン・料金',            en: 'Personal Pricing',          path: '/personal/pricing' },
        { ja: '経費精算',             en: 'Reimbursement',             path: '/features/reimbursement-reports' },
        { ja: '1年間無料体験を始める',  en: 'Start 1-Year Free Trial',   path: `${prefix}/free-trial` },
      ],
    },
    {
      key: 'government', labelJa: '官公庁', labelEn: 'Gov & Public-Sector', icon: Shield,
      target: '/government',
      tone: { bg: 'bg-[#0066CC]', text: 'text-[#04244a]', linkActive: 'text-[#0066CC]', barColor: '#0066CC', hoverBg: 'hover:bg-sky-50', hoverText: 'hover:text-[#0066CC]', activeBg: 'bg-sky-100', activeText: 'text-[#0066CC]' },
      desc: ja ? '官公庁・公共部門' : 'Official fleets · gov · public-sector',
      items: [
        { ja: '官公庁・公共部門 概要',      en: 'Government Overview',           path: '/government' },
        { ja: 'リソース・参考資料',      en: 'Resources & References',        path: '/government-vehicle-toll-resources-japan' },
        { ja: '機能',                   en: 'Features',                      path: '/government/features' },
        { ja: '組織管理',                en: 'Organization Management',       path: '/government/organization-management', badge: { ja: '主要機能', en: 'Key' } },
        { ja: '登録車両リスト',          en: 'Registered Vehicle Registry',   path: '/government/authorized-vehicles' },
        { ja: '目的外利用の確認',     en: 'Misuse Review',              path: '/features/authorized-vehicle-monitoring' },
        { ja: '料金',                   en: 'Pricing',                       path: '/government/pricing' },
        { ja: '1年間無料体験を始める',   en: 'Start 1-Year Free Trial',       path: `${prefix}/free-trial` },
      ],
    },
  ];

  return (
    <div className="hidden md:inline-flex items-center gap-0.5 p-1 rounded-full bg-slate-100/70 ring-1 ring-slate-200/80" data-testid="audience-switcher">
      {tracks.map(({ key, labelJa, labelEn, icon: IconCmp, target, tone, desc, items }) => {
        const active = audience === key;
        const activeBg = active ? `${tone.bg} text-white shadow-md` : 'text-slate-600 hover:text-slate-900 hover:bg-white';
        return (
          <div
            key={key}
            className="relative"
            onMouseEnter={() => openMenu(key)}
            onMouseLeave={schedClose}
          >
            <button
              type="button"
              onClick={() => { setAudience(key); setHovered(null); navigate(target); }}
              className={`relative inline-flex items-center justify-center gap-1.5 h-7 px-3 text-[12px] font-extrabold tracking-wide rounded-full whitespace-nowrap ${activeBg}`}
              data-testid={`audience-switch-${key}`}
            >
              <IconCmp className="w-3.5 h-3.5" strokeWidth={2.4} />
              <span>{ja ? labelJa : labelEn}</span>
            </button>

            {hovered === key && (
              <div
                className="absolute top-full left-1/2 -translate-x-1/2 pt-1.5 z-[9990]"
                onMouseEnter={() => openMenu(key)}
                onMouseLeave={schedClose}
                role="menu"
                data-testid={`audience-menu-${key}`}
              >
                <div
                  className="min-w-[260px] bg-white border border-slate-200 rounded-xl py-2 dropdown-animate"
                  style={{ boxShadow: '0 12px 40px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06)' }}
                >
                <div className="px-3.5 py-1.5 border-b border-slate-100 mb-1">
                  <span className="block h-0.5 w-8 rounded-full mb-1.5" style={{ background: tone.barColor }} />
                  <p className={`text-[10px] font-black uppercase tracking-[0.2em] ${tone.text}`}>
                    {ja ? labelJa : labelEn}
                  </p>
                  <p className="text-[11px] text-slate-500 leading-snug mt-0.5">{desc}</p>
                </div>
                {items.map((it, idx) => {
                  if (it.divider) {
                    return <div key={idx} className="my-1 mx-3.5 border-t border-slate-100" aria-hidden="true" />;
                  }
                  const isOnPath = normPath(location.pathname) === normPath(it.path);
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => { setAudience(key); setHovered(null); navigate(it.path); }}
                      style={isOnPath ? { boxShadow: `inset 3px 0 0 ${tone.barColor}` } : undefined}
                      className={`w-full text-left px-3.5 py-2 text-[13px] transition-colors ${
                        isOnPath
                          ? `${tone.activeBg} ${tone.activeText} font-bold`
                          : `font-semibold text-slate-700 ${tone.hoverBg} ${tone.hoverText}`
                      }`}
                      data-testid={`audience-menu-${key}-item-${idx}`}
                      aria-current={isOnPath ? 'page' : undefined}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        {ja ? it.ja : it.en}
                        {it.badge && (
                          <span className={`text-[8.5px] font-black uppercase tracking-wide px-1.5 py-0.5 rounded-full ${tone.activeBg} ${tone.activeText}`}>
                            {ja ? it.badge.ja : it.badge.en}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * AudienceSubnav — persistent in-page subnav for Business / Personal /
 * Government sections. Sits directly under the AudienceModeStrip and
 * carries that audience's contextual links so users never have to
 * re-open the header dropdown to jump between pages.
 *
 * Consistent ordering across all three tracks:
 *   1. Overview
 *   2. Features
 *   3. Special tool page (AVR / Reimbursement / Plate Types / Family List)
 *   4. Pricing
 *   5. Start / Apply
 *
 * Horizontally scrollable on mobile. Audience accent on active pill.
 */
function AudienceSubnav({ ja, prefix }) {
  const { audience } = useAudience();
  const location = useLocation();
  const path = location.pathname || '';

  if (path.startsWith('/dashboard') || path.startsWith('/admin')) return null;
  if (audience !== 'personal' && audience !== 'business' && audience !== 'government') return null;

  // Iter 92 (final) — Subnav uses a distinct palette from the audience
  // brand color (set by the user): Business=blue, Personal=purple,
  // Government=green. This makes the in-page subnav visually distinct
  // from the AudienceModeStrip directly above it.
  const tone = audience === 'business'
    ? { activeBg: 'bg-[#0066CC]',    activeText: 'text-white', activeRing: 'ring-[#0066CC]/30', hoverText: 'hover:text-[#04244a]', barColor: '#0066CC' }
    : audience === 'government'
      ? { activeBg: 'bg-emerald-600', activeText: 'text-white', activeRing: 'ring-emerald-600/30', hoverText: 'hover:text-emerald-700', barColor: '#16a34a' }
      : { activeBg: 'bg-[#9200C7]',   activeText: 'text-white', activeRing: 'ring-[#9200C7]/30', hoverText: 'hover:text-[#5b0080]', barColor: '#9200C7' };

  const items = audience === 'business' ? [
    { slug: 'overview',                   label: ja ? '概要'             : 'Overview',                    path: '/business' },
    { slug: 'features',                   label: ja ? '機能'             : 'Features',                    path: '/business/features' },
    { slug: 'organization-management',    label: ja ? '組織管理'         : 'Organization Management',     path: '/business/organization-management' },
    { slug: 'authorized-vehicle-registry', label: ja ? '登録車両リスト' : 'Registered Vehicle Registry', path: '/business/authorized-vehicles' },
    { slug: 'misuse-detection',           label: ja ? '目的外利用の確認'      : 'Misuse Review',             path: '/features/authorized-vehicle-monitoring' },
    { slug: 'pricing',                    label: ja ? '料金'             : 'Pricing',                     path: '/business/pricing' },
    { slug: 'start-free-trial',           label: ja ? '1年間無料体験を始める' : 'Start 1-Year Free Trial',  path: `${prefix}/free-trial` },
  ] : audience === 'government' ? [
    { slug: 'overview',                   label: ja ? '概要'             : 'Overview',                    path: '/government' },
    { slug: 'resources',                  label: ja ? 'リソース・参考資料' : 'Resources & References',      path: '/government-vehicle-toll-resources-japan' },
    { slug: 'features',                   label: ja ? '機能'             : 'Features',                    path: '/government/features' },
    { slug: 'organization-management',    label: ja ? '組織管理'         : 'Organization Management',     path: '/government/organization-management' },
    { slug: 'authorized-vehicle-registry', label: ja ? '登録車両リスト' : 'Registered Vehicle Registry', path: '/government/authorized-vehicles' },
    { slug: 'misuse-detection',           label: ja ? '目的外利用の確認'      : 'Misuse Review',             path: '/features/authorized-vehicle-monitoring' },
    { slug: 'pricing',                    label: ja ? '料金'             : 'Pricing',                     path: '/government/pricing' },
    { slug: 'start-free-trial',           label: ja ? '1年間無料体験を始める' : 'Start 1-Year Free Trial',  path: `${prefix}/free-trial` },
  ] : [
    { slug: 'overview',                   label: ja ? '概要'             : 'Overview',          path: '/personal' },
    { slug: 'features',                   label: ja ? '機能'             : 'Features',          path: '/personal/features' },
    { slug: 'reimbursement',              label: ja ? '経費精算'          : 'Reimbursement',     path: '/features/reimbursement-reports' },
    { slug: 'family-vehicle-list',        label: ja ? '家族登録車両リスト' : 'Family Vehicle List', path: '/personal/family-vehicle-list' },
    { slug: 'pricing',                    label: ja ? '料金'             : 'Pricing',           path: '/personal/pricing' },
    { slug: 'start-free-trial',           label: ja ? '1年間無料体験を始める' : 'Start 1-Year Free Trial',  path: `${prefix}/free-trial` },
  ];

  // Active if path is exact-match OR (for /business etc.) the current
  // path equals the target prefix. Normalize the language prefix so
  // /en/free-trial and /ja/free-trial both match /{prefix}/free-trial.
  // Avoid marking Overview as active on every sub-page.
  const stripLang = (p) => (p || '').replace(/^\/(ja|en)(?=\/|$)/, '') || '/';
  const isActive = (target) => {
    if (!target || target.includes('?')) return false;
    const a = stripLang(path);
    const b = stripLang(target);
    return a === b;
  };

  // Iter 117 — Mobile UX redesign (replaces the Iter-116 auto-scroll attempt).
  // Customer feedback: "that menu is not made for mobile submenu". Switching
  // from a horizontal-scrolling pill strip to a wrap-into-rows pill grid so
  // every link is visible at once with no scrolling needed on phones. On md+
  // widths, the pills sit on a single centered row exactly like before.
  return (
    <div
      className="bg-white border-b border-slate-200/80 shadow-[0_1px_0_0_rgba(15,23,42,0.04)]"
      data-testid={`audience-subnav-${audience}`}
      style={{ position: 'relative', zIndex: 30 }}
    >
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6">
        <div className="flex flex-wrap items-center justify-center gap-x-1 gap-y-1.5 py-2">
          <span
            className="hidden md:inline-flex flex-shrink-0 w-1 h-4 rounded-full mr-1.5"
            style={{ backgroundColor: tone.barColor }}
            aria-hidden="true"
          />
          {items.map((it, idx) => {
            const active = isActive(it.path);
            return (
              <Link
                key={idx}
                to={it.path}
                className={`inline-flex items-center min-h-[44px] px-2.5 py-1 rounded-full text-[11.5px] sm:text-[12px] font-bold whitespace-nowrap transition-all ${
                  active
                    ? `${tone.activeBg} ${tone.activeText} ring-2 ${tone.activeRing}`
                    : `text-slate-600 ${tone.hoverText} hover:bg-slate-50`
                }`}
                data-testid={`audience-subnav-${audience}-${it.slug}`}
                aria-current={active ? 'page' : undefined}
              >
                {it.label}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * AudienceModeStrip — a slim band directly under the main header that
 * makes it unmistakable which audience mode the visitor is currently in.
 * Personal: emerald accent. Business: purple accent. Hidden in default
 * (homepage) mode so the chrome stays clean for first-time visitors.
 */
function AudienceModeStrip({ ja }) {
  const { audience } = useAudience();
  const location = useLocation();
  const path = location.pathname || '';

  // Hide on authenticated dashboard / admin surfaces only. Auth
  // pages (login / register) still show the mode strip so the
  // visitor can see + change which track they came from.
  if (path.startsWith('/dashboard') || path.startsWith('/admin')) {
    return null;
  }
  // Hide in default mode — homepage is intentionally track-neutral.
  if (audience !== 'personal' && audience !== 'business' && audience !== 'government') return null;

  const isBiz = audience === 'business';
  const isGov = audience === 'government';
  const tone = isGov
    ? { bg: 'bg-[#0066CC]', accent: 'bg-sky-200', icon: Shield, labelJa: '官公庁・公共部門モード', labelEn: 'Government & Public-Sector Mode' }
    : isBiz
      ? { bg: 'bg-[#9200C7]', accent: 'bg-amber-300', icon: Building2, labelJa: '法人モード', labelEn: 'Business Mode' }
      : { bg: 'bg-emerald-600', accent: 'bg-emerald-200', icon: User, labelJa: '個人モード', labelEn: 'Personal Mode' };
  const Icon = tone.icon;

  return (
    <div className={`${tone.bg} text-white`} data-testid={`audience-mode-strip-${audience}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center gap-2 py-1 flex-wrap">
          <span className="inline-flex items-center gap-2 whitespace-nowrap">
            <Icon className="w-3 h-3" strokeWidth={2.5} />
            <span className="text-[10px] font-black uppercase tracking-[0.22em]">
              {ja ? tone.labelJa : tone.labelEn}
            </span>
          </span>
          <span className={`inline-block w-1 h-1 rounded-full ${tone.accent}`} />
          <span className="text-[10px] font-semibold opacity-80 whitespace-nowrap">
            {isGov
              ? (ja ? '公用車・公務車両向けナビ' : 'Official-fleet navigation')
              : isBiz
                ? (ja ? '法人・車両管理' : 'Fleet-focused navigation')
                : (ja ? '個人ユーザー' : 'Individual-focused navigation')}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * TopUtilityStrip — slim, low-emphasis bar above the main header that
 * holds quick-access utility links (FAQ, Contact, Help, Resources,
 * Sitemap, Phishing Alerts). Keeps the main header focused on the
 * audience-specific nav.
 */
function TopUtilityStrip({ ja, prefix, lang, onToggleLang }) {
  const [resOpen, setResOpen] = useState(false);
  const resTimer = useRef(null);
  const openRes = () => { if (resTimer.current) clearTimeout(resTimer.current); setResOpen(true); };
  const closeRes = () => { resTimer.current = setTimeout(() => setResOpen(false), 180); };
  const guidesListPath = `${prefix}/guides/list`;
  const onGuidesClick = useSameRouteScroll(guidesListPath);

  const resources = [
    { label: ja ? '代表メッセージ' : 'Founder’s Message', href: `${prefix}/founders-message`, external: false, highlight: true },
    { label: ja ? 'エンジニアリング構成' : 'Our Architecture Engineering', href: `${prefix}/architecture`, external: false, highlight: true, Icon: Workflow },
    { label: ja ? 'ETC利用照会サービス（MEISAI）（公式）' : 'MEISAI (official)', href: 'https://www.etc-meisai.jp/', external: true },
    { label: ja ? 'ETCマイレージ登録' : 'ETC Mileage',           href: 'https://www.smile-etc.jp/',  external: true },
    { label: 'NEXCO East',                                 href: 'https://www.e-nexco.co.jp/', external: true },
    { label: 'NEXCO Central',                              href: 'https://www.c-nexco.co.jp/', external: true },
    { label: 'NEXCO West',                                 href: 'https://www.w-nexco.co.jp/', external: true },
    { label: ja ? '首都高／阪神／本四' : 'Shutoko / Hanshin / HSBE', href: 'https://www.shutoko.jp/', external: true },
    { label: ja ? 'ETCセットアップ店' : 'ETC Setup Shops', href: 'https://www.go-etc.jp/shop_search', external: true },
    { label: ja ? '通行料金・経路検索' : 'Toll & Route',      href: 'https://www.driveplaza.com/dp/SearchTop', external: true },
    { label: ja ? 'すべてのリソース →' : 'See all resources →', href: `${prefix}/official-etc-resources`, external: false },
  ];

  const itemClass = "px-2.5 py-1 text-[11px] font-medium text-gray-600 hover:text-[#9200C7] hover:bg-purple-50/60 rounded-md transition-colors";

  return (
    <div className="hidden lg:block bg-gray-50 border-b border-gray-200/70" data-testid="top-utility-strip">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-8">
          <div className="flex items-center gap-0.5">
            <Link to={`${prefix}/faq`} className={itemClass} data-testid="util-faq">
              <span className="inline-flex items-center gap-1.5"><HelpCircle className="w-3 h-3" />{ja ? 'よくあるご質問' : 'FAQ'}</span>
            </Link>
            <Link to={guidesListPath} onClick={onGuidesClick} className={itemClass} data-testid="util-guides">
              <span className="inline-flex items-center gap-1.5"><BookOpen className="w-3 h-3 text-[#9200C7]" />{ja ? 'ガイド一覧' : 'All Guides'}</span>
            </Link>
            <Link to={`${prefix}/contact`} className={itemClass} data-testid="util-contact">
              <span className="inline-flex items-center gap-1.5"><Mail className="w-3 h-3" />{ja ? 'お問い合わせ' : 'Contact'}</span>
            </Link>
            <div className="relative" onMouseEnter={openRes} onMouseLeave={closeRes}>
              <button type="button" className={`${itemClass} inline-flex items-center gap-1.5`} data-testid="util-resources-btn">
                <ExternalLink className="w-3 h-3" />
                {ja ? 'リソース' : 'Resources'}
                <ChevronDown className={`w-2.5 h-2.5 transition-transform ${resOpen ? 'rotate-180' : ''}`} />
              </button>
              {resOpen && (
                <div className="absolute top-full left-0 mt-0.5 min-w-[260px] bg-white border border-gray-200 rounded-lg py-1.5 shadow-lg z-[9995]" data-testid="util-resources-dropdown">
                  {resources.map((r, i) => (
                    r.external ? (
                      <a key={i} href={r.href} target="_blank" rel="noopener noreferrer"
                        className="flex items-center justify-between gap-2 px-3 py-1.5 text-[12px] text-gray-700 hover:text-[#9200C7] hover:bg-purple-50/60 transition-colors">
                        <span>{r.label}</span>
                        <ExternalLink className="w-3 h-3 text-gray-400" />
                      </a>
                    ) : (
                      <Link key={i} to={r.href}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold transition-colors ${
                          r.highlight
                            ? 'text-[#9200C7] bg-purple-50 border-b border-purple-100 hover:bg-purple-100'
                            : 'text-gray-700 hover:text-[#9200C7] hover:bg-purple-50/60'
                        }`}>
                        {r.highlight && (r.Icon
                          ? <r.Icon className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={2.2} />
                          : <BookOpen className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={2.2} />)}
                        <span>{r.label}</span>
                      </Link>
                    )
                  ))}
                </div>
              )}
            </div>
            <Link to="/sitemap" className={itemClass} data-testid="util-sitemap">
              {ja ? 'サイトマップ' : 'Sitemap'}
            </Link>
            <Link to="/testimonials" className={itemClass} data-testid="util-testimonials">
              <span className="inline-flex items-center gap-1.5"><Star className="w-3 h-3 text-amber-500" strokeWidth={2.4} />{ja ? 'お客様の声' : 'Testimonials'}</span>
            </Link>
            <Link to={`${prefix}/etc-phishing-safety`} className={itemClass} data-testid="util-phishing">
              <span className="inline-flex items-center gap-1.5"><ShieldAlert className="w-3 h-3 text-amber-600" />{ja ? 'フィッシング詐欺にご注意' : 'Phishing Alerts'}</span>
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="https://www.facebook.com/JapanTollReceipts"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full bg-[#1877F2] text-white text-[11px] font-bold hover:bg-[#0f63d1] shadow-sm transition-colors"
              data-testid="util-facebook"
              aria-label={ja ? 'Facebookページを開く（新しいタブ）' : 'Open our Facebook page (new tab)'}
              title={ja ? 'Facebookでいいね！' : 'Like us on Facebook'}
            >
              <Facebook className="w-3 h-3" strokeWidth={2.4} />
              <span>{ja ? 'いいね！' : 'Like'}</span>
            </a>
            <InlinePrototypeCounters />
            <a href="tel:046-874-7156" className="text-[11px] font-semibold text-gray-600 hover:text-emerald-700 inline-flex items-center gap-1.5" data-testid="util-phone">
              <Phone className="w-3 h-3" />046-874-7156
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Logged-in user menu — minimal pill that navigates to dashboard on click,
 * shows a clean dropdown on hover.
 */
function UserMenu({ user, ja, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const closeTimer = useRef(null);
  const navigate = useNavigate();
  const isAdminOrHost = user.role === 'admin' || user.role === 'host';
  const dashHome = isAdminOrHost ? '/admin' : '/dashboard';
  const settingsLink = isAdminOrHost ? '/admin' : '/dashboard/profile';

  const openMenu = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setOpen(true);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 180);
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const label = user.user_id_display || (user.role === 'host' ? 'HOST' : user.role === 'admin' ? 'ADMIN' : (user.name || user.email));
  const roleTag = user.role === 'host' ? 'HOST' : user.role === 'admin' ? 'ADMIN' : null;

  // Avatar initial — first character of the customer's name (or email
  // local-part as a sensible fallback). Uppercased and trimmed so an
  // email like "billing@..." renders as "B" instead of "b".
  const avatarInitial = (() => {
    const src = (user.name && user.name.trim()) || (user.email && user.email.split('@')[0]) || '?';
    return src.trim().charAt(0).toUpperCase();
  })();

  return (
    <div className="relative" ref={ref} onMouseEnter={openMenu} onMouseLeave={scheduleClose}>
      <button
        type="button"
        onClick={() => navigate(dashHome)}
        className="group flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-xs font-semibold transition-all shadow-sm hover:shadow-md ring-1 ring-blue-700/30 hover:ring-blue-800/40"
        data-testid="user-menu-btn"
        aria-label={ja ? 'ダッシュボード' : 'Dashboard'}
      >
        {/* Avatar disc — white-on-blue initial; the User icon shows
            up as a subtle backdrop so the disc always reads as a
            person even when the initial is non-Latin (e.g. JA names). */}
        <span className="relative flex items-center justify-center w-7 h-7 rounded-full bg-white/95 text-blue-700 font-black text-[11px] shadow-inner ring-1 ring-white/40 flex-shrink-0">
          <User className="absolute inset-0 m-auto w-3.5 h-3.5 text-blue-200/70" strokeWidth={2} aria-hidden="true" />
          <span className="relative">{avatarInitial}</span>
        </span>
        <span className="font-mono tracking-wide leading-none" data-testid="user-menu-jtr">{label}</span>
        {roleTag && (
          <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wider leading-none ${
            user.role === 'host' ? 'bg-amber-300 text-slate-900' : 'bg-rose-300 text-slate-900'
          }`}>{roleTag}</span>
        )}
        <ChevronDown className="w-3 h-3 text-blue-100 transition-transform group-hover:translate-y-px" strokeWidth={2.5} />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-1.5 w-56 rounded-lg bg-white border border-slate-200 shadow-lg overflow-hidden z-50"
          data-testid="user-menu-dropdown"
          onMouseEnter={openMenu}
          onMouseLeave={scheduleClose}
        >
          <div className="px-3 py-2 border-b border-slate-100 bg-slate-50">
            <p className="text-[11px] text-slate-600 truncate" title={user.email}>{user.email}</p>
          </div>
          <div className="py-1">
            <Link
              to={dashHome}
              onClick={() => setOpen(false)}
              className="flex items-center gap-2.5 px-3 py-2 text-xs font-medium text-slate-700 hover:text-violet-700 hover:bg-violet-50 transition-colors"
              data-testid="user-menu-dashboard"
            >
              <LayoutDashboard className="w-3.5 h-3.5 text-slate-400" strokeWidth={2} />
              {ja ? 'ダッシュボード' : 'Dashboard'}
            </Link>
            <Link
              to={settingsLink}
              onClick={() => setOpen(false)}
              className="flex items-center gap-2.5 px-3 py-2 text-xs font-medium text-slate-700 hover:text-violet-700 hover:bg-violet-50 transition-colors"
              data-testid="user-menu-settings"
            >
              <Settings className="w-3.5 h-3.5 text-slate-400" strokeWidth={2} />
              {ja ? '設定' : 'Settings'}
            </Link>
            <div className="my-1 border-t border-slate-100" />
            <button
              type="button"
              onClick={() => { setOpen(false); onLogout(); }}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-medium text-rose-600 hover:bg-rose-50 transition-colors"
              data-testid="user-menu-logout"
            >
              <LogOut className="w-3.5 h-3.5" strokeWidth={2} />
              {ja ? 'ログアウト' : 'Logout'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * CorporateSubnav — persistent company/trust subnav. The corporate &
 * "who we are" pages (Corporate Profile, Founder's Message, About, Our
 * Team, Contact, Roadmap, Changelog) previously had no shared navigation,
 * so jumping between them meant going back to the footer each time. This
 * slim strip sits in the fixed header on those routes only, giving every
 * company page a consistent way to move between the others.
 *
 * Wrap-into-rows pill grid (mobile-friendly), slate corporate accent,
 * active pill highlighted. Hidden on dashboard/admin surfaces.
 */
const CORPORATE_NAV_ITEMS = [
  { slug: 'corporate-profile', path: '/corporate-profile', en: 'Corporate Profile', ja: '会社概要' },
  { slug: 'founders-message',  path: '/founders-message',  en: 'Founder’s Message', ja: '代表メッセージ' },
  { slug: 'about',             path: '/about',             en: 'About',             ja: '私たちについて' },
  { slug: 'our-team',          path: '/our-team',          en: 'Our Team',          ja: 'チーム紹介', altPaths: ['/team'] },
  { slug: 'contact',           path: '/contact',           en: 'Contact',           ja: 'お問い合わせ' },
];

function CorporateSubnav({ ja, prefix }) {
  const location = useLocation();
  const path = location.pathname || '';
  if (path.startsWith('/dashboard') || path.startsWith('/admin')) return null;

  const stripLang = (p) => ((p || '').replace(/^\/(ja|en)(?=\/|$)/, '').replace(/\/+$/, '') || '/');
  const here = stripLang(path);

  // Only render on a corporate/company page.
  const allCorporatePaths = CORPORATE_NAV_ITEMS.flatMap((it) => [it.path, ...(it.altPaths || [])]);
  if (!allCorporatePaths.includes(here)) return null;

  const isActive = (it) => here === it.path || (it.altPaths || []).includes(here);

  return (
    <div
      className="bg-white border-b border-slate-200/80 shadow-[0_1px_0_0_rgba(15,23,42,0.04)]"
      data-testid="corporate-subnav"
      style={{ position: 'relative', zIndex: 30 }}
    >
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6">
        <div className="flex flex-wrap items-center justify-center gap-x-1 gap-y-1.5 py-2">
          <span className="hidden md:inline-flex items-center gap-1.5 flex-shrink-0 mr-2 text-[11px] font-black uppercase tracking-[0.16em] text-slate-400">
            <Building2 className="w-3.5 h-3.5 text-indigo-500" strokeWidth={2.4} />
            {ja ? '会社情報' : 'Company'}
          </span>
          {CORPORATE_NAV_ITEMS.map((it) => {
            const active = isActive(it);
            return (
              <Link
                key={it.slug}
                to={`${prefix}${it.path}`}
                className={`inline-flex items-center min-h-[44px] px-2.5 py-1 rounded-full text-[11.5px] sm:text-[12px] font-bold whitespace-nowrap transition-all ${
                  active
                    ? 'bg-indigo-600 text-white ring-2 ring-indigo-600/30'
                    : 'text-slate-600 hover:text-indigo-700 hover:bg-slate-50'
                }`}
                data-testid={`corporate-subnav-${it.slug}`}
                aria-current={active ? 'page' : undefined}
              >
                {ja ? it.ja : it.en}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function Header() {
  const { lang, setLang, toggleLang } = useLanguage();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // Global same-route refresh delegate. Catches clicks on any internal
  // anchor whose pathname equals the current location and (a) smooth-
  // scrolls to top + (b) dispatches `jtr:same-page-refresh` so pages
  // can reset their own paginated / filtered state. Header.jsx mounts
  // on every route so calling this here is equivalent to a global
  // app-level mount, without touching App.js.
  useSamePageRefresh();
  const [open, setOpen] = useState(false);
  const [hoveredMenu, setHoveredMenu] = useState(null);
  // Iter 131 — dynamic header spacer. The previous static heights
  // (`h-[88px] lg:h-[146px]` etc.) under-measured the real sticky
  // stack on narrow mobile viewports because the DevBanner wraps
  // onto 2 lines, and the AudienceSubnav wraps onto 2-3 rows on
  // small phones. The result was the page hero getting hidden
  // behind the white logo header. We now observe the actual height
  // of the fixed wrapper and apply it to the spacer in real time.
  const headerWrapperRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const ja = lang === 'ja';
  const prefix = `/${lang}`;

  // Iter 165 — scroll-shrink effect removed by owner request. The
  // header now stays a single, consistent height regardless of scroll
  // position. No `scroll` listener, no `scrolled` state, no class swap.

  useEffect(() => {
    const el = headerWrapperRef.current;
    if (!el) return;
    const update = () => {
      // Use bounding rect for fractional pixels and a small safety
      // margin (+1px) to avoid a hairline overlap from sub-pixel rounding.
      const h = Math.ceil(el.getBoundingClientRect().height);
      setHeaderHeight(h);
      // Iter 147 — expose the dynamic header height as a CSS variable
      // so any page with `sticky top: var(--jtr-header-h)` content
      // (filter rows, FAQ category jump nav, etc.) automatically tracks
      // the real header instead of hard-coding pixel values.
      if (typeof document !== 'undefined') {
        document.documentElement.style.setProperty('--jtr-header-h', `${h}px`);
      }
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => { ro.disconnect(); window.removeEventListener('resize', update); };
  }, [user, location.pathname]);

  useEffect(() => {
    const detected = detectLangFromPath(location.pathname);
    if (detected) setLang(detected);
  }, [location.pathname, setLang]);

  const handleLogout = async () => { await logout(); navigate('/'); };

  // Toggle language AND update URL to match (locale continuity — see
  // swapLangPrefix in lib/i18n for the single authoritative rule).
  const handleToggleLang = () => {
    const newLang = lang === 'ja' ? 'en' : 'ja';
    toggleLang();
    const newPath = swapLangPrefix(location.pathname, newLang);
    // Preserve query string + hash so tokenised pages (e.g. the Early Access
    // confirmation link `/early-access/confirm?token=…`, email preferences,
    // and invite links) don't lose their token on a language toggle — which
    // previously turned a successful confirmation into a false "Invalid link".
    const suffix = `${location.search || ''}${location.hash || ''}`;
    navigate(`${newPath}${suffix}`, { replace: true });
  };

  // Iter 92 (final) — Simple, decision-focused global header. Three
  // direct links only: Features · How It Works · Free Weekly. No
  // mega menus. Pricing / Resources / Security all live in audience
  // sections, top utility strip, and footer (not the main header).
  // This is intentional: the header is a landing-page header, not an
  // admin-software menu.
  const { audience } = useAudience();

  // Trust / corporate pages should read as stable company documents, not
  // marketing landing pages. On these routes we suppress the public launch
  // countdown ribbons (mobile LaunchCountdownBanner + desktop DevBanner).
  // Normalize away the /en|/ja prefix and any trailing slash before matching.
  const TRUST_PAGE_PATHS = ['/corporate-profile', '/about', '/contact', '/founders-message', '/our-team', '/team'];
  const normalizedTrustPath = (location.pathname || '')
    .replace(/^\/(en|ja)(?=\/|$)/, '')
    .replace(/\/+$/, '') || '/';
  const isTrustPage = TRUST_PAGE_PATHS.includes(normalizedTrustPath);

  // Avoid a redundant auth CTA: don't show the header "Sign In" button while
  // already on the login page, nor the "Try Free" button while on the
  // signup/free-trial page. Pure presentational — no auth/flow change.
  const onLoginPage = normalizedTrustPath === '/login';
  const onSignupPage = normalizedTrustPath === '/free-trial' || normalizedTrustPath === '/register';

  const mainNav = [
    { label: ja ? '機能'         : 'Features',     icon: Sparkles, isFlat: true, path: '/features' },
    { label: ja ? 'ご利用の流れ'       : 'How It Works', icon: Compass,  isFlat: true, path: `${prefix}/how-it-works` },
    { label: ja ? '無料週次レポート' : 'Free Weekly',  icon: Gift,     isFlat: true, path: '/free-weekly', activeColor: 'blue' },
  ];

  return (
    <>
      {/* Skip-to-main-content link (Professional Polish Audit, 2026-02,
          PPA-T10) — the header/promo nav that precedes the page's actual
          content on EVERY route has 15+ links before it (FAQ, guides,
          resources, sitemap, testimonials, social, phone...). Without
          this, a keyboard/screen-reader user reaching /login had to Tab
          through all of them (27 presses) before reaching the email
          field. Visually hidden until focused (first Tab press reveals
          it); jumps straight to `#main-content`, which page components
          should set on their primary content wrapper (LoginPage does).
          Standard WCAG 2.4.1 "Bypass Blocks" pattern. */}
      <a
        href="#main-content"
        onClick={(e) => {
          // Professional Polish Audit (2026-02, PPA-T10 follow-up): a plain
          // href="#main-content" anchor only SCROLLS the page — the browser
          // does not move keyboard focus onto a non-interactive <main> unless
          // it is programmatically focusable. Without this, the very next Tab
          // press after activating the skip link continued from the TOP of
          // the DOM (the skip link itself), defeating its purpose. We now
          // explicitly give the target a tabIndex (if it doesn't have one)
          // and call .focus() so the NEXT Tab press lands on the first real
          // control inside the page's main content (e.g. the login email
          // field), not back at the marketing header links.
          const target = document.getElementById('main-content');
          if (target) {
            e.preventDefault();
            if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
            target.focus({ preventScroll: false });
            target.scrollIntoView({ block: 'start' });
          }
        }}
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[300] focus:px-4 focus:py-2 focus:rounded-lg focus:bg-slate-900 focus:text-white focus:text-sm focus:font-bold focus:shadow-xl focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-2"
        data-testid="skip-to-main-content-link"
      >
        {ja ? 'メインコンテンツへスキップ' : 'Skip to main content'}
      </a>
      {/* Fixed header wrapper */}
      <div ref={headerWrapperRef} className="fixed top-0 left-0 right-0 z-[200]" data-testid="site-header-wrapper" data-print-hide="true">
        {!isTrustPage && <LaunchCountdownBanner />}
        {!isTrustPage && <DevBanner />}
        {/* Iter 65 → 66 — Inline prototype counters: the dual P/B counter
            now lives inside the TopUtilityStrip (next to the phone number)
            instead of taking its own banner row. Compacts the sticky
            header stack and frees vertical space for content. */}
        <TopUtilityStrip ja={ja} prefix={prefix} lang={lang} onToggleLang={handleToggleLang} />
        <header className="bg-white border-b border-gray-100" data-testid="main-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative flex items-center justify-between h-[60px] lg:h-[68px]">

            {/* Mobile-only live Early Access stats (top-left). Desktop shows
                the equivalent in TopUtilityStrip. */}
            <MobileEarlyAccessStats prefix={prefix} />

            {/* Logo with positioning text */}
            <Link to="/" onClick={(e) => { if (location.pathname === '/') { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); } }} className="flex items-center gap-2 flex-shrink-0 absolute left-1/2 -translate-x-1/2 lg:static lg:translate-x-0" data-testid="header-logo">
              <img src="/assets/logo/japantollreceipts.gif" alt="Japan Toll Receipts" className="w-auto object-contain h-10 lg:h-12" />
            </Link>

            {/* Iter 91 — AudienceSwitcher restored. Each track pill is
                now a hover-dropdown showing all pages in that track,
                so clicking "Business" both flips the audience context
                AND surfaces every Business page. */}
            <div className="hidden lg:flex items-center ml-4 xl:ml-5">
              <AudienceSwitcher ja={ja} prefix={prefix} />
            </div>

            {/* Desktop nav - centered with controlled spacing */}
            <nav className="hidden lg:flex items-center justify-center gap-1 flex-1 mx-4 xl:mx-6" data-testid="main-nav">
              {mainNav.map((menu, i) => {
                // Iter 65 — flat (single-link) menu items render as a
                // direct Link with no dropdown. They're used for the
                // Personal/Business audience-aware mode where each
                // section already maps 1:1 to a single page.
                if (menu.isFlat) {
                  const flatActive = location.pathname === menu.path
                                     || location.pathname === `${prefix}${menu.path}`;
                  const MenuIcon = menu.icon;
                  // Iter 65d — audience-aware active accent. Personal
                  // keeps the brand purple. Business switches to blue
                  // so the active menu pill doesn't clash with the
                  // purple Business audience toggle on the same row.
                  const isBiz = audience === 'business';
                  const isGov = audience === 'government';
                  const activeBg     = isGov ? 'bg-[#0066CC] hover:bg-[#0052a3]'
                                       : isBiz ? 'bg-[#0083D3] hover:bg-[#0070b8]'
                                       : 'bg-[#9200C7] hover:bg-[#7b00a8]';
                  const hoverIdleTxt = isGov ? 'hover:text-[#0066CC]'
                                       : isBiz ? 'hover:text-[#0083D3]'
                                       : 'hover:text-[#9200C7]';
                  // Per-item active color override. Examples:
                  //   - Free Weekly uses blue regardless of track
                  //   - Government Overview uses green so it pops against
                  //     the blue Government track (matches the inverted
                  //     accent pattern of Personal/Business)
                  const itemActiveBg = menu.activeColor === 'blue'
                    ? 'bg-[#0083D3] hover:bg-[#0070b8]'
                    : menu.activeColor === 'green'
                      ? 'bg-emerald-600 hover:bg-emerald-700'
                      : activeBg;
                  return (
                    <Link
                      key={i}
                      to={menu.path}
                      className={`relative flex items-center gap-1.5 text-[13px] font-semibold transition-colors px-3 py-2 rounded-lg whitespace-nowrap ${
                        menu.highlight
                          ? 'bg-[#62BB46] text-white hover:bg-[#56a83e] shadow-sm'
                          : menu.urgent
                            ? menu.urgentColor === 'emerald'
                              ? flatActive
                                ? 'bg-emerald-600 text-white shadow-sm ring-2 ring-emerald-200'
                                : 'bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-300 font-bold'
                              : flatActive
                                ? 'bg-amber-500 text-white shadow-sm ring-2 ring-amber-200'
                                : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
                            : flatActive
                              ? `text-white ${itemActiveBg} shadow-sm`
                              : `text-slate-700 ${hoverIdleTxt} hover:bg-gray-50`
                      }`}
                      data-testid={`nav-${menu.label.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      {menu.urgent && !flatActive && (
                        <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2">
                          <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${menu.urgentColor === 'emerald' ? 'bg-emerald-400' : 'bg-amber-400'} opacity-75`} />
                          <span className={`relative inline-flex rounded-full h-2 w-2 ${menu.urgentColor === 'emerald' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                        </span>
                      )}
                      {MenuIcon && <MenuIcon className="w-3.5 h-3.5 opacity-90" strokeWidth={2} />}
                      <span>{menu.label}</span>
                    </Link>
                  );
                }

                // Iter 90 — dropdown active when either:
                //  (1) a sub-item path is the current location, OR
                //  (2) the menu carries a `pathPrefix` AND we're under it.
                // The `!mainNav.slice(0,i).some(...)` guard prevents two
                // dropdowns from BOTH lighting up if multiple cross-track
                // entries share the same path (e.g. /business/authorized-vehicles
                // is also listed inside the shared "Features" dropdown).
                const matchedByItem = (menu.items || []).some(item => location.pathname === item.path);
                const matchedByPrefix = menu.pathPrefix
                  ? (location.pathname === menu.pathPrefix || location.pathname.startsWith(`${menu.pathPrefix}/`))
                  : false;
                const isActive = (matchedByItem || matchedByPrefix) &&
                  !mainNav.slice(0, i).some(prev =>
                    (prev.items && prev.items.some(item => location.pathname === item.path))
                    || (prev.pathPrefix && (location.pathname === prev.pathPrefix || location.pathname.startsWith(`${prev.pathPrefix}/`)))
                  );
                // Active accent color per menu — purple default, green
                // for Personal, blue for Government.
                const activeAccent = menu.activeColor === 'green'
                  ? 'text-emerald-700 bg-emerald-50/60'
                  : menu.activeColor === 'blue'
                    ? 'text-[#0066CC] bg-sky-50/60'
                    : 'text-[#9200C7] bg-purple-50/60';
                const hoverAccent = menu.activeColor === 'green'
                  ? 'hover:text-emerald-700'
                  : menu.activeColor === 'blue'
                    ? 'hover:text-[#0066CC]'
                    : 'hover:text-[#9200C7]';
                return (
                <Fragment key={i}>
                  <div className="relative group"
                    onMouseEnter={() => setHoveredMenu(i)}
                    onMouseLeave={() => setHoveredMenu(null)}>
                    <button className={`flex items-center gap-1.5 text-[13px] font-semibold transition-colors px-2.5 xl:px-3 py-2 rounded-lg whitespace-nowrap ${isActive ? activeAccent : `text-slate-700 ${hoverAccent} hover:bg-gray-50`}`}
                      data-testid={`nav-${menu.label.toLowerCase().replace(/\s/g, '-')}`}>
                      {menu.icon && <menu.icon className="w-3.5 h-3.5 opacity-80" strokeWidth={2} />}
                      <span>{menu.label}</span>
                      <ChevronDown className={`w-3 h-3 opacity-60 transition-transform ${hoveredMenu === i ? 'rotate-180' : ''}`} strokeWidth={2.5} />
                    </button>
                    {/* Invisible bridge to prevent dropdown from closing when moving mouse down */}
                    {hoveredMenu === i && <div className="absolute top-full left-0 right-0 h-2" />}
                    {hoveredMenu === i && (
                      <div className={`absolute top-[calc(100%+4px)] ${menu.columns === 2 ? 'left-1/2 -translate-x-1/2 min-w-[520px]' : 'left-1/2 -translate-x-1/2 min-w-[320px]'} bg-white border border-gray-200 rounded-xl py-2 z-[9990] dropdown-animate`} style={{ boxShadow: '0 12px 40px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06)' }}>
                        {menu.desc && (
                          <div className={`px-3.5 ${menu.columns === 2 ? 'pb-2 pt-1' : 'pb-2 pt-1'} ${menu.columns === 2 ? '' : 'mb-1 border-b border-gray-100'}`}>
                            <p className="text-[11px] text-slate-500 leading-snug">{menu.desc}</p>
                          </div>
                        )}
                        <div className={menu.columns === 2 ? 'grid grid-cols-2 gap-x-1' : ''}>
                          {menu.items.map((item, j) => {
                            // Iter 55d — divider rows act as a section break
                            // inside a 2-column dropdown. They span both columns
                            // (col-span-2) and render as a thin separator with
                            // an uppercase label, so secondary/legal items sit
                            // visually below the primary block.
                            if (item.divider) {
                              return (
                                <div key={j} className="col-span-full mx-3.5 my-2 flex items-center gap-2.5">
                                  <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-400 whitespace-nowrap">
                                    {item.label}
                                  </span>
                                  <span className="flex-1 h-px bg-gray-200" />
                                </div>
                              );
                            }
                            const itemActive = location.pathname === item.path;
                            const ItemIcon = item.icon;
                            // Iter 55c — `external: true` items open in a
                            // new tab and render with an ExternalLink badge.
                            // Used for the Official ETC Resources dropdown
                            // so customers can verify our pass-through
                            // positioning ("we link OUT to the official
                            // services, we are not them").
                            if (item.external) {
                              return (
                                <a key={j} href={item.path}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={() => setHoveredMenu(null)}
                                  className="flex items-start gap-3 px-3.5 py-2.5 mx-1 rounded-lg transition-colors group hover:bg-purple-50/60"
                                  data-testid={`nav-external-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                                >
                                  {ItemIcon && (
                                    <span className="flex-shrink-0 mt-0.5 w-7 h-7 rounded-md flex items-center justify-center bg-gray-50 text-gray-400 group-hover:bg-[#9200C7]/10 group-hover:text-[#9200C7] transition-colors">
                                      <ItemIcon className="w-3.5 h-3.5" strokeWidth={2} />
                                    </span>
                                  )}
                                  <span className="flex-1 min-w-0">
                                    <span className="block text-[13px] font-semibold text-slate-800 group-hover:text-[#9200C7] transition-colors flex items-center gap-1">
                                      {item.label}
                                      <ExternalLink className="w-2.5 h-2.5 text-slate-300 group-hover:text-[#9200C7] transition-colors" strokeWidth={2.5} />
                                    </span>
                                    {item.desc && <span className="block text-[11px] text-gray-400 mt-0.5">{item.desc}</span>}
                                  </span>
                                </a>
                              );
                            }
                            return (
                            <Link key={j} to={item.path}
                              onClick={() => setHoveredMenu(null)}
                              className={`flex items-start gap-3 px-3.5 py-2.5 mx-1 rounded-lg transition-colors group ${itemActive ? 'bg-green-50' : 'hover:bg-purple-50/60'}`}>
                              {ItemIcon && (
                                <span className={`flex-shrink-0 mt-0.5 w-7 h-7 rounded-md flex items-center justify-center transition-colors ${
                                  itemActive
                                    ? 'bg-[#62BB46]/15 text-[#62BB46]'
                                    : 'bg-gray-50 text-gray-400 group-hover:bg-[#9200C7]/10 group-hover:text-[#9200C7]'
                                }`}>
                                  <ItemIcon className="w-3.5 h-3.5" strokeWidth={2} />
                                </span>
                              )}
                              <span className="flex-1 min-w-0">
                                <span className={`block text-[13px] font-semibold transition-colors ${itemActive ? 'text-[#62BB46]' : 'text-slate-800 group-hover:text-[#9200C7]'}`}>{item.label}</span>
                                {item.desc && <span className="block text-[11px] text-gray-400 mt-0.5">{item.desc}</span>}
                              </span>
                            </Link>
                          );})}
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Iter 65 — flat Pricing link removed: Pricing is now
                      surfaced under both Personal and Business dropdowns
                      (and the mobile Quick Links grid), so the top-level
                      duplicate just crowded the bar. */}
                </Fragment>
              );})}
            </nav>

            {/* Right: language + auth — unified pill height + consistent spacing */}
            <div className="hidden lg:flex items-center gap-2" data-testid="header-right-cluster">
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleToggleLang}
                      className="inline-flex items-center justify-center gap-1 h-9 px-3 rounded-full text-[11px] font-bold text-white bg-[#62BB46] hover:bg-[#56a83e] shadow-sm hover:shadow-md ring-1 ring-[#56a83e]/40 transition-all"
                      aria-label={lang === 'ja' ? 'Switch language to English' : 'Switch language to Japanese'}
                      data-testid="header-lang-toggle"
                    >
                      <Globe className="w-3 h-3" strokeWidth={2.4} />
                      <span className="tracking-[0.14em]">{lang === 'ja' ? 'EN' : 'JA'}</span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="bottom"
                    align="end"
                    sideOffset={6}
                    className="text-[11px] font-medium"
                    data-testid="header-lang-toggle-tooltip"
                  >
                    {lang === 'ja' ? 'Switch language to English' : 'Switch language to Japanese'}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {user ? (
                <>
                  <UserMenu user={user} ja={ja} onLogout={handleLogout} />
                </>
              ) : (
                <>
                  {!onLoginPage && (
                    <Link to="/login">
                      <button className="inline-flex items-center justify-center h-11 sm:h-9 px-4 rounded-full text-[12px] font-bold text-slate-700 bg-white hover:bg-slate-50 ring-1 ring-slate-200 hover:ring-slate-300 transition-all whitespace-nowrap" data-testid="login-btn">
                        {ja ? 'ログイン' : 'Sign In'}
                      </button>
                    </Link>
                  )}
                  {!onSignupPage && (
                    <Link to={`${prefix}/free-trial`}>
                      <button className="inline-flex items-center justify-center h-11 sm:h-9 px-5 rounded-full bg-[#0083D3] text-white text-[12px] font-extrabold hover:bg-[#006fb3] shadow-sm hover:shadow-md transition-all btn-premium whitespace-nowrap" data-testid="register-btn">
                        {ja ? '無料体験' : 'Try Free'}
                      </button>
                    </Link>
                  )}
                </>
              )}
            </div>

            {/* Mobile header buttons */}
            <div className="lg:hidden flex items-center gap-1 ml-auto">
              <button
                onClick={handleToggleLang}
                className="inline-flex items-center gap-1 min-h-[44px] px-2.5 text-xs font-bold text-[#9200C7] hover:bg-purple-50 rounded-lg"
                data-testid="header-lang-toggle-mobile"
                aria-label={ja ? 'Switch to English' : '日本語に切り替え'}
              >
                <Globe className="w-4 h-4" strokeWidth={1.8} />
                {lang === 'ja' ? 'EN' : 'JA'}
              </button>
              <button onClick={() => setOpen(!open)} className="p-2.5 text-gray-600 hover:bg-gray-100 rounded-lg" data-testid="mobile-menu-btn">
                {open ? <X className="w-5 h-5" strokeWidth={2} /> : <Menu className="w-5 h-5" strokeWidth={2} />}
              </button>
            </div>

          </div>
        </div>
      </header>
      <AudienceModeStrip ja={ja} />
      <AudienceSubnav ja={ja} prefix={prefix} />
      <CorporateSubnav ja={ja} prefix={prefix} />
      </div>
      {/* Spacer for fixed header. Iter 131 — dynamic height based on
          a real-time ResizeObserver measurement of the fixed wrapper.
          This replaces the previous brittle static heights which
          undermeasured the stack on narrow mobile viewports (causing
          page heroes to slide under the white logo header on phones). */}
      <div
        style={{ height: headerHeight ? `${headerHeight}px` : undefined }}
        className={headerHeight ? '' : 'h-[88px] lg:h-[146px]'}
        data-testid="header-spacer"
        aria-hidden="true"
      />

      {/* Mobile menu - full-screen overlay */}
      {open && (
        <div className="fixed inset-0 z-[9000] lg:hidden overflow-y-auto bg-white" data-testid="mobile-menu">
          {/* Header with logo + language + close */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
            <img src="/assets/logo/japantollreceipts.gif" alt="JTR" className="h-9 w-auto" />
            <div className="flex items-center gap-2">
              <button onClick={() => { handleToggleLang(); }} className="flex items-center gap-1.5 text-sm font-semibold text-[#9200C7] px-3 py-2 rounded-lg bg-purple-50">
                <Globe className="w-4 h-4" strokeWidth={1.5} />
                {lang === 'ja' ? 'English' : '日本語'}
              </button>
              <button onClick={() => setOpen(false)} className="p-2 text-gray-500 hover:text-gray-800">
                <X className="w-6 h-6" strokeWidth={2} />
              </button>
            </div>
          </div>

          {/* Quick Links - Purple gradient area */}
          <div className="mx-4 mt-4 rounded-2xl p-5" style={{ background: 'linear-gradient(135deg, #9200C7, #7b00a8)' }}>
            <p className="text-center text-white text-xs font-bold uppercase tracking-[0.2em] mb-4">QUICK LINKS</p>
            <div className="grid grid-cols-2 gap-3">
              <Link to={`${prefix}/free-weekly`} onClick={() => setOpen(false)} className="flex items-center justify-center gap-2 px-3 py-3 bg-white/20 rounded-xl text-white text-sm font-medium">
                <Zap className="w-4 h-4" /> {ja ? '無料レポート' : 'Free Reports'}
              </Link>
              <Link to={`${prefix}/pricing`} onClick={() => setOpen(false)} className="flex items-center justify-center gap-2 px-3 py-3 bg-white/20 rounded-xl text-white text-sm font-medium">
                <CreditCard className="w-4 h-4" /> {ja ? '料金' : 'Pricing'}
              </Link>
              <Link to={`${prefix}/faq`} onClick={() => setOpen(false)} className="flex items-center justify-center gap-2 px-3 py-3 bg-white/20 rounded-xl text-white text-sm font-medium">
                <HelpCircle className="w-4 h-4" /> FAQ
              </Link>
              <Link to={`${prefix}/contact`} onClick={() => setOpen(false)} className="flex items-center justify-center gap-2 px-3 py-3 bg-white/20 rounded-xl text-white text-sm font-medium">
                <Mail className="w-4 h-4" /> {ja ? 'お問い合わせ' : 'Contact'}
              </Link>
            </div>
            <a href="tel:046-874-7156" className="flex items-center justify-center gap-2 mt-3 py-3 bg-[#62BB46] rounded-xl text-white text-sm font-bold">
              <Phone className="w-4 h-4" /> Call: 046-874-7156
            </a>
          </div>

          {/* Icon Grid - 2x2 cards. Iter 117: Pricing + FAQs removed from this
              grid since they already appear in the QUICK LINKS purple area
              above (customer screenshot pointed out the duplicates). Replaced
              with Features + Sitemap so the grid still has 4 distinct entry
              points. */}
          <div className="px-4 mt-4 grid grid-cols-2 gap-3">
            <Link to={`${prefix}/how-it-works`} onClick={() => setOpen(false)} className="flex flex-col items-center justify-center py-5 bg-purple-50 rounded-xl border border-purple-100">
              <svg className="w-7 h-7 text-[#9200C7] mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><polygon points="5 3 19 12 5 21 5 3" /></svg>
              <span className="text-sm font-medium text-gray-700">{ja ? 'ご利用の流れ' : 'How It Works'}</span>
            </Link>
            <Link to={`${prefix}/features`} onClick={() => setOpen(false)} className="flex flex-col items-center justify-center py-5 bg-green-50 rounded-xl border border-green-100">
              <Sparkles className="w-7 h-7 text-[#62BB46] mb-2" strokeWidth={1.5} />
              <span className="text-sm font-medium text-gray-700">{ja ? '機能' : 'Features'}</span>
            </Link>
            <Link to="/testimonials" onClick={() => setOpen(false)} className="flex flex-col items-center justify-center py-5 bg-blue-50 rounded-xl border border-blue-100">
              <MessageSquare className="w-7 h-7 text-blue-600 mb-2" strokeWidth={1.5} />
              <span className="text-sm font-medium text-gray-700">{ja ? 'お客様の声' : 'Testimonials'}</span>
            </Link>
            <Link to={`${prefix}/sitemap`} onClick={() => setOpen(false)} className="flex flex-col items-center justify-center py-5 bg-amber-50 rounded-xl border border-amber-100">
              <Compass className="w-7 h-7 text-amber-600 mb-2" strokeWidth={1.5} />
              <span className="text-sm font-medium text-gray-700">{ja ? 'サイトマップ' : 'Sitemap'}</span>
            </Link>
          </div>

          {/* Audience selector at top of mobile menu */}
          <div className="px-4 mt-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500 mb-2">{ja ? '表示モード' : 'Browse mode'}</p>
            <div className="grid grid-cols-4 gap-2">
              <button onClick={() => { setOpen(false); navigate('/'); }}
                className="py-2.5 rounded-lg border border-gray-200 bg-white text-[11px] font-bold text-gray-700"
                data-testid="mobile-mode-default">
                <Compass className="w-4 h-4 mx-auto mb-1" />{ja ? '概要' : 'Home'}
              </button>
              <button onClick={() => { setOpen(false); navigate('/business'); }}
                className="py-2.5 rounded-lg border border-purple-200 bg-white text-[11px] font-bold text-[#9200C7]"
                data-testid="mobile-mode-business">
                <Building2 className="w-4 h-4 mx-auto mb-1" />{ja ? '法人' : 'Business'}
              </button>
              <button onClick={() => { setOpen(false); navigate('/personal'); }}
                className="py-2.5 rounded-lg border border-emerald-200 bg-emerald-50 text-[11px] font-bold text-emerald-700"
                data-testid="mobile-mode-personal">
                <User className="w-4 h-4 mx-auto mb-1" />{ja ? '個人' : 'Personal'}
              </button>
              <button onClick={() => { setOpen(false); navigate('/government'); }}
                className="py-2.5 rounded-lg border border-blue-200 bg-white text-[11px] font-bold text-[#0066CC]"
                data-testid="mobile-mode-government">
                <Shield className="w-4 h-4 mx-auto mb-1" />{ja ? '官公庁' : 'Gov & Public'}
              </button>
            </div>
          </div>

          {/* Expandable sections (audience-aware) — MOVED ABOVE All Pages so
              users see the primary nav items (Features, How It Works,
              Free Weekly) and CTA right after the audience selector,
              before they have to scroll past the comprehensive All-Pages
              directory. */}
          <div className="px-4 mt-4 space-y-2">
            {mainNav.map((menu, i) => {
              if (menu.isFlat) {
                const MenuIcon = menu.icon;
                return (
                  <Link
                    key={i}
                    to={menu.path}
                    onClick={() => setOpen(false)}
                    className={`flex items-center gap-2 px-4 py-3.5 rounded-xl border ${
                      menu.highlight
                        ? 'bg-[#62BB46] text-white border-[#56a83e]'
                        : 'border-gray-200 bg-white text-[#9200C7]'
                    } text-base font-medium`}
                  >
                    {MenuIcon && <MenuIcon className="w-4 h-4" strokeWidth={2} />}
                    <span>{menu.label}</span>
                  </Link>
                );
              }
              return (
              <details key={i} className="group rounded-xl border border-gray-200 overflow-hidden">
                <summary className="flex items-center justify-between px-4 py-3.5 cursor-pointer list-none">
                  <span className="flex items-center gap-2 text-base font-medium" style={{ color: '#9200C7' }}>
                    {menu.label}
                  </span>
                  <ChevronDown className="w-5 h-5 text-[#9200C7] group-open:rotate-180 transition-transform" strokeWidth={1.5} />
                </summary>
                <div className="px-4 pb-3 space-y-1">
                  {(menu.items || []).map((item, j) => (
                    item.divider ? (
                      <div key={j} className="flex items-center gap-2 pt-3 pb-1">
                        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-400">
                          {item.label}
                        </span>
                        <span className="flex-1 h-px bg-gray-200" />
                      </div>
                    ) : item.external ? (
                      <a key={j} href={item.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between py-2.5 text-sm text-gray-600 hover:text-[#9200C7] transition-colors"
                        onClick={() => setOpen(false)}>
                        <span>{item.label}</span>
                        <ExternalLink className="w-3.5 h-3.5 text-gray-400" strokeWidth={2} />
                      </a>
                    ) : (
                      <Link key={j} to={item.path}
                        className="block py-2.5 text-sm text-gray-600 hover:text-[#9200C7] transition-colors"
                        onClick={() => setOpen(false)}>
                        {item.label}
                      </Link>
                    )
                  ))}
                </div>
              </details>
              );
            })}
          </div>

          {/* Primary CTA (Try Free / Dashboard) — MOVED ABOVE All Pages so
              the customer's call-to-action is visible right after Free
              Weekly without having to scroll through 11 expandable
              accordions first. */}
          <div className="px-4 py-4">
            {user ? (
              <div className="space-y-2">
                <div className="px-4 py-3 rounded-xl bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-200">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-violet-700">
                    {user.role === 'host' ? 'HOST' : user.role === 'admin' ? 'ADMIN' : 'CUSTOMER'}
                  </p>
                  <p className="text-sm font-bold text-gray-900 truncate">{user.name || user.email}</p>
                  {user.user_id_display && (
                    <p className="text-xs font-mono text-violet-700 mt-0.5">{user.user_id_display}</p>
                  )}
                </div>
                <Link
                  to={(user.role === 'admin' || user.role === 'host') ? '/admin' : '/dashboard'}
                  onClick={() => setOpen(false)}
                  className="block"
                >
                  <button className="w-full py-3.5 rounded-xl bg-[#9200C7] text-white text-sm font-bold" data-testid="mobile-dashboard-btn">
                    {ja ? 'ダッシュボード' : 'Go to Dashboard'}
                  </button>
                </Link>
                <button
                  type="button"
                  onClick={() => { setOpen(false); handleLogout(); }}
                  className="w-full py-3.5 rounded-xl bg-rose-50 text-rose-700 border border-rose-200 text-sm font-bold flex items-center justify-center gap-2"
                  data-testid="mobile-logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                  {ja ? 'ログアウト' : 'Logout'}
                </button>
              </div>
            ) : (
              <Link to={`${prefix}/free-trial`} onClick={() => setOpen(false)} className="block">
                <button className="w-full py-3.5 rounded-xl bg-[#0083D3] text-white text-sm font-bold btn-premium" data-testid="mobile-register-btn">
                  {ja ? '1年間プレミアム無料で試す' : 'See 1-Year Premium Free'}
                </button>
              </Link>
            )}
          </div>

          {/* ── All Pages directory (matches Nojo Enterprise / JapanETCcard
              pattern — every public page grouped by section). Pulls from
              the canonical /app/frontend/src/data/siteNav.js so the mobile
              menu, footer, and /sitemap stay in lockstep. Now sits BELOW
              the primary nav items + CTA so the user lands on the most
              important actions first. */}
          <div className="px-4 mt-2 pb-[calc(96px+env(safe-area-inset-bottom))]">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">
                {ja ? 'すべてのページ' : 'All Pages'}
              </p>
              <Link
                to={resolveNavPath('/sitemap', lang)}
                onClick={() => setOpen(false)}
                className="text-[11px] font-semibold text-[#9200C7] hover:underline"
                data-testid="mobile-all-pages-sitemap-link"
              >
                {ja ? 'サイトマップ →' : 'Full sitemap →'}
              </Link>
            </div>
            <div className="space-y-2">
              {SITE_NAV_SECTIONS.map((sec) => {
                const tone = SECTION_COLOR_CLASSES[sec.color] || SECTION_COLOR_CLASSES.slate;
                return (
                  <details
                    key={sec.key}
                    className="group rounded-xl border border-gray-200 overflow-hidden bg-white"
                    data-testid={`mobile-sitenav-section-${sec.key}`}
                  >
                    <summary className="flex items-center justify-between px-4 py-3 cursor-pointer list-none">
                      <span className="flex items-center gap-2.5 min-w-0">
                        <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${tone.dot}`} />
                        <span className="flex flex-col min-w-0">
                          <span className={`text-[14px] font-bold ${tone.text} truncate`}>
                            {sec.title[lang] || sec.title.en}
                          </span>
                          {sec.subtitle && (
                            <span className="text-[11px] text-gray-500 truncate">
                              {sec.subtitle[lang] || sec.subtitle.en}
                            </span>
                          )}
                        </span>
                      </span>
                      <ChevronDown
                        className="w-4 h-4 text-gray-400 flex-shrink-0 group-open:rotate-180 transition-transform"
                        strokeWidth={2}
                      />
                    </summary>
                    <div className={`px-3 pb-2 ${tone.bg}`}>
                      <ul className="divide-y divide-white/60">
                        {sec.items.map((it, j) => {
                          const isExternal = it.external || (it.path || '').startsWith('http');
                          const dest = isExternal ? it.path : resolveNavPath(it.path, lang);
                          const label = it.label[lang] || it.label.en;
                          const highlightClass =
                            it.highlight === 'green'  ? 'text-emerald-700 font-semibold' :
                            it.highlight === 'purple' ? 'text-[#9200C7] font-semibold' :
                            it.highlight === 'amber'  ? 'text-amber-700 font-semibold' :
                            'text-gray-700';
                          return (
                            <li key={j}>
                              {isExternal ? (
                                <a
                                  href={dest}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`flex items-center justify-between gap-2 py-2.5 px-2 text-[13px] ${highlightClass} hover:bg-white/70 rounded-md transition-colors`}
                                  onClick={() => setOpen(false)}
                                >
                                  <span className="truncate">{label}</span>
                                  <ExternalLink className="w-3 h-3 text-gray-400 flex-shrink-0" strokeWidth={2} />
                                </a>
                              ) : (
                                <Link
                                  to={dest}
                                  onClick={() => setOpen(false)}
                                  className={`block py-2.5 px-2 text-[13px] ${highlightClass} hover:bg-white/70 rounded-md transition-colors`}
                                >
                                  {label}
                                </Link>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </details>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
